import React, { useEffect, useState } from "react";
import { useLocation, useSearchParams, Redirect } from "wouter";
import { Show } from "@clerk/react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Wifi, Building2 } from "lucide-react";

const HIGHLIGHTS = [
  "Earn commission on every fibre connection",
  "Track your orders and payouts in real time",
  "Get support from your account manager",
];

const REFERRAL_KEY = "mooya_ref";

interface MasterDealer {
  id: number;
  name: string;
  dealerCode: string;
}

export function JoinPage() {
  const [, setLocation] = useLocation();
  const [params] = useSearchParams();
  const refCode = params.get("ref") ?? "";
  const [md, setMd] = useState<MasterDealer | null>(null);
  const [mdLoading, setMdLoading] = useState(!!refCode);

  useEffect(() => {
    if (!refCode) return;
    // Store the referral code so it survives the Clerk sign-up redirect.
    localStorage.setItem(REFERRAL_KEY, refCode.toUpperCase());
    fetch(`/api/master-dealers/referral/${encodeURIComponent(refCode.toUpperCase())}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data: MasterDealer | null) => setMd(data))
      .catch(() => setMd(null))
      .finally(() => setMdLoading(false));
  }, [refCode]);

  return (
    <>
      <Show when="signed-in">
        <Redirect to="/home" />
      </Show>
      <Show when="signed-out">
        <div className="font-sans min-h-[100dvh] w-full max-w-[480px] mx-auto bg-[#0B132B] text-white flex flex-col">
          <header className="px-6 pt-8 pb-4 flex items-center gap-2">
            <img
              src={`${import.meta.env.BASE_URL}mooya-mark.png`}
              alt="Mooya"
              className="w-8 h-8 object-contain"
            />
            <span className="font-bold text-lg tracking-tight">Mooya</span>
          </header>

          <main className="flex-1 flex flex-col px-6 pt-6">
            <div className="w-12 h-12 rounded-full bg-[#E6007E]/15 flex items-center justify-center mb-6">
              <Wifi className="w-6 h-6 text-[#E6007E]" />
            </div>
            <h1 className="text-3xl font-bold tracking-tight mb-3">
              Become a Mooya fibre agent
            </h1>

            {!mdLoading && md && (
              <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-3 mb-6">
                <Building2 className="w-4 h-4 text-[#00A859] shrink-0" />
                <p className="text-sm text-slate-200">
                  Referred by{" "}
                  <span className="font-semibold text-white">{md.name}</span>
                </p>
              </div>
            )}

            <p className="text-slate-300 mb-8">
              Create your account to start onboarding. It only takes a few
              minutes.
            </p>

            <ul className="space-y-3 mb-10">
              {HIGHLIGHTS.map((h) => (
                <li key={h} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#00A859] mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-200">{h}</span>
                </li>
              ))}
            </ul>
          </main>

          <footer className="px-6 pb-10 space-y-3">
            <Button
              onClick={() => setLocation("/sign-up")}
              className="w-full bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl py-6 text-base font-semibold"
            >
              Get started
            </Button>
            <button
              onClick={() => setLocation("/sign-in")}
              className="w-full text-sm text-slate-300 hover:text-white transition-colors py-2"
            >
              Already have an account? Sign in
            </button>
          </footer>
        </div>
      </Show>
    </>
  );
}
